import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../auth/AuthProvider';
import { getPendingShareAccept, clearPendingShareAccept, PENDING_SHARE_EVENT } from '../pendingShare';
import { resumePendingShareAccept } from '../resumePendingShare';

// Cap on automatic retries for one queued token. resumePendingShareAccept
// re-queues (and re-emits PENDING_SHARE_EVENT) on retryable failures, which
// this component reacts to — without a cap, a persistent failure (e.g. the
// endpoint down) would retry in a tight loop with no backoff.
const MAX_ATTEMPTS = 3;

// App-root owner of the queued "Add to my courses" intent. Whenever the user is
// authenticated AND subscribed AND a share acceptance is pending, it fires the
// accept exactly once and drops them into the new course — regardless of which
// route they happen to be on. This is what makes the flow survive a brand-new
// user's signup path (which may not route back through /shared/:token or
// /subscription/success). It only ever acts at the terminal, subscribed step,
// so it never traps an unsubscribed user or spends a token before payment.
export default function PendingShareResumer() {
  const navigate = useNavigate();
  const { user, isSubscribed, subscriptionLoading, enabled } = useAuth();
  const [building, setBuilding] = useState(false);
  const [failed, setFailed] = useState(false);
  const inFlight = useRef(false);
  const attemptsRef = useRef(0);
  const lastTokenRef = useRef(null);

  const maybeResume = useCallback(async () => {
    if (inFlight.current) return;
    if (subscriptionLoading) return;      // wait until subscription state resolves
    if (!user || !isSubscribed) return;   // only fire at the subscribed terminal step
    const token = getPendingShareAccept({ uid: user.uid });
    if (!token) return;                   // nothing queued for this user

    if (token !== lastTokenRef.current) {
      lastTokenRef.current = token;
      attemptsRef.current = 0;
    }

    inFlight.current = true;
    setBuilding(true);
    setFailed(false);
    const dest = await resumePendingShareAccept({ authEnabled: enabled, uid: user.uid });
    inFlight.current = false;
    setBuilding(false);

    if (dest) {
      navigate(dest);
      return;
    }

    // Failed. If retryable, resumePendingShareAccept already re-queued the
    // token and emitted PENDING_SHARE_EVENT, which fires another attempt via
    // the listener below — up to MAX_ATTEMPTS. Otherwise (or once the cap is
    // hit) stop and show a dismissable error instead of looping silently.
    attemptsRef.current += 1;
    const stillQueued = getPendingShareAccept({ uid: user.uid });
    if (stillQueued && attemptsRef.current < MAX_ATTEMPTS) return;
    if (stillQueued) clearPendingShareAccept();
    setFailed(true);
  }, [user, isSubscribed, subscriptionLoading, enabled, navigate]);

  // React to auth / subscription state changes (post sign-in, post checkout).
  useEffect(() => {
    maybeResume();
  }, [maybeResume]);

  // React to a pending intent being set with no state change — e.g. an already
  // subscribed user clicking "Add to my courses", or a retryable-failure requeue.
  useEffect(() => {
    const handler = () => maybeResume();
    window.addEventListener(PENDING_SHARE_EVENT, handler);
    return () => window.removeEventListener(PENDING_SHARE_EVENT, handler);
  }, [maybeResume]);

  if (!building && !failed) return null;

  if (failed) {
    return (
      <div
        role="alert"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10000,
          background: 'var(--bg-primary)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          textAlign: 'center',
          padding: 24,
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
          We couldn't add that course
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', maxWidth: 360, lineHeight: 1.6 }}>
          Something went wrong building it on your account. You can open the share link again to retry.
        </div>
        <button
          type="button"
          onClick={() => setFailed(false)}
          style={{
            padding: '10px 20px',
            borderRadius: 999,
            background: 'var(--btn-primary-bg)',
            color: 'var(--btn-primary-text)',
            fontSize: 14,
            fontWeight: 600,
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Dismiss
        </button>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'var(--bg-primary)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        textAlign: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          border: '3px solid var(--border-subtle)',
          borderTopColor: 'var(--accent-primary)',
          borderRadius: '50%',
          animation: 'graspd-spin 0.8s linear infinite',
        }}
      />
      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
        Adding your course…
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', maxWidth: 360, lineHeight: 1.6 }}>
        Building a fresh copy on your account. This takes a moment.
      </div>
    </div>
  );
}
